/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
// eslint-disable-next-line no-restricted-imports
import Router from 'next/router';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {info} from '@ion/core/src/logger/logger';
import changePage, {getRoute, isRoute, Route} from '@/routes/RouteDefinitions';
import {NavState} from '@/store/reducers/nav';
import {ApplicationKeys} from '@/types/application';
import {Page} from '@/types/pages';
import {preinitialization} from './initialization';

export const SET_PAGE                  = 'SET_PAGE';
export const SET_SIDE_SHEET_STATUS     = 'SET_SIDE_SHEET_STATUS';
export const TOGGLE_SECONDARY_NAV      = 'TOGGLE_SECONDARY_NAV';
export const TOGGLE_USER_PROFILE_VIEW  = 'TOGGLE_USER_PROFILE_VIEW';
export const PAGE_SECURITY_INITIALIZED = 'PAGE_SECURITY_INITIALIZED';

/**
 * The setPage action is the initial action that is invoked when an iOn page
 * is loaded. The first call will determine if the pre-initialization phase
 * has been completed; if not, the application pre-initialization process
 * is invoked.
 */
export const setPage = page => {
  return (dispatch, getState) => {
    if (!getState().initialization.preInitializationComplete) {
      dispatch(preinitialization());
    }
    const {pages}: NavState = getState().nav;
    if (isPermittedPage(pages, page)) {
      if (!isRoute(Route.LOGIN)) {
        localPersistence(ApplicationKeys.BMWPortalPage, 'route', page.split('?')?.[0]);
      }
      dispatch({type: SET_PAGE, page});
    }
  };
};

/**
 * Invoked exclusively via the initializationComplete action creator upon completion
 * of the application initialization phase, at which point a route change listener is
 * added to the Next.js Router which, upon a route change event, verifies that the
 * User has permission to view the given route / page.
 */
export const initializePageSecurity = () => {
  return (dispatch, getState) => {
    if (!getState().nav.pageSecurityInitialized) {
      Router.events.on('routeChangeStart', function(url) {
        verifyRouteChange(url, dispatch, getState);
      });
      info('Page Security initialized');
      dispatch({type: PAGE_SECURITY_INITIALIZED});
    }
  };
};

/**
 * @function toggleSecondaryNav
 */
export const toggleSecondaryNav = (show: boolean, index: number) => (
  {type: TOGGLE_SECONDARY_NAV, show, index}
);

/**
 * @setSideSheetStatus
 */
export const setSideSheetStatus = data => (
  {type: SET_SIDE_SHEET_STATUS, data}
);

/**
 * @verifyRouteChange
 */
export const verifyRouteChange = (url: string, dispatch: Function, getState: Function) => {
  const {pages, currentPage}: NavState = getState().nav;
  if (url !== currentPage) {
    if (isPermittedPage(pages, url)) {
      dispatch(setPage(url));
    } else {
      currentPage ? changePage(currentPage as Route) : Router.back();
      throw `[BMW Portal Security Violation]: Route ${url} is not permitted. This error can safely be ignored`;
    }
  }
};

/**
 * @isPermittedPage
 */
const isPermittedPage = (pages: Page[], uri: string): boolean => {
  const hasPage = (pages: Page[]): boolean => {
    return Boolean(pages.find(({route}: Page) => {
      return uri.includes(getRoute(route)) || uri.includes(route);
    }));
  };
  return hasPage(pages);
};
